import styled, { keyframes, css } from 'styled-components';

// ============================================
// COLORS - Purple Design System
// ============================================
export const colors = {
  primary: '#59249B',
  primaryLight: '#854EE0',
  primaryLighter: '#C8ACFD',
  primaryBg: '#F3EEFF',
  secondary: '#00A2DD',
  success: '#34C759',
  successLight: '#E8F8ED',
  error: '#FF3B30',
  errorLight: '#FFEBEA',
  warning: '#FF9500',
  background: '#F5F5F7',
  card: '#FFFFFF',
  text: '#393939',
  textLight: '#6B6F77',
  textMuted: '#AAADB1',
  border: '#DDDDDD',
  borderLight: '#EEEEEE',
};

// ============================================
// ANIMATIONS
// ============================================
export const slideInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

export const slideInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

export const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

export const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

// ============================================
// LAYOUT COMPONENTS
// ============================================
export const PageContainer = styled.div`
  min-height: 100vh;
  width: 100%;
  min-width: 0;
  background: ${colors.background};
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
`;

export const Header = styled.header`
  background: ${colors.card};
  min-height: 64px;
  padding: 8px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid ${colors.borderLight};
  position: sticky;
  top: 0;
  z-index: 100;
`;

export const HeaderLogo = styled.img`
  height: 28px;
`;

export const HeaderTitle = styled.h1`
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: 600;
  color: ${colors.textLight};
  margin: 0;
`;

export const BackButton = styled.button`
  background: none;
  border: none;
  width: 44px;
  height: 44px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.textLight};
  cursor: pointer;
  
  &:active {
    opacity: 0.7;
  }
`;

export const Content = styled.main`
  flex: 1;
  width: 100%;
  max-width: 440px;
  margin: 0 auto;
  padding: 16px;
  animation: ${fadeIn} 0.3s ease-out;
`;

export const StepContainer = styled.div<{ direction?: 'left' | 'right' }>`
  animation: ${props => props.direction === 'left' ? slideInLeft : slideInRight} 0.3s ease-out;
`;

// ============================================
// STEP INDICATOR
// ============================================
export const StepIndicatorContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 16px;
  background: ${colors.card};
  margin: -16px -16px 20px -16px;
  border-bottom: 1px solid ${colors.borderLight};
`;

export const StepDot = styled.div<{ active?: boolean; completed?: boolean }>`
  width: 28px;
  height: 28px;
  flex: 0 0 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.3s ease;
  
  ${props => props.completed && css`
    background: ${colors.success};
    color: white;
  `}
  
  ${props => props.active && !props.completed && css`
    background: ${colors.primary};
    color: white;
  `}
  
  ${props => !props.active && !props.completed && css`
    background: ${colors.borderLight};
    color: ${colors.textMuted};
  `}
`;

export const StepLine = styled.div<{ completed?: boolean }>`
  width: 28px;
  flex: 1 1 28px;
  max-width: 40px;
  height: 2px;
  background: ${props => props.completed ? colors.success : colors.borderLight};
  transition: background 0.3s ease;
`;

// ============================================
// SECTION COMPONENTS
// ============================================
export const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
`;

export const SectionTitle = styled.h2`
  font-size: 24px;
  line-height: 1.2;
  font-weight: 600;
  color: ${colors.primary};
  margin: 0;
  white-space: normal;
`;

export const SectionDivider = styled.div`
  flex: 1;
  height: 1px;
  background: ${colors.primaryLighter};
`;

export const SectionDescription = styled.p`
  font-size: 14px;
  color: ${colors.textLight};
  margin: 0 0 16px 0;
  line-height: 1.5;
`;

// ============================================
// FORM COMPONENTS
// ============================================
export const FormGroup = styled.div`
  margin-bottom: 16px;
`;

export const Label = styled.label<{ required?: boolean }>`
  display: block;
  font-size: 16px;
  line-height: 20px;
  font-weight: 600;
  color: ${colors.text};
  margin-bottom: 8px;
  letter-spacing: 0;
  
  ${props => props.required && css`
    &::after {
      content: ' *';
      color: ${colors.error};
    }
  `}
`;

export const Input = styled.input<{ hasError?: boolean }>`
  width: 100%;
  height: 48px;
  padding: 0 16px;
  border: 1px solid ${props => props.hasError ? colors.error : colors.border};
  border-radius: 4px;
  font-size: 16px;
  color: ${colors.text};
  background: ${colors.card};
  transition: border-color 0.2s, box-shadow 0.2s;
  
  &::placeholder {
    color: ${colors.textMuted};
  }
  
  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? colors.error : colors.primary};
    box-shadow: 0 0 0 3px ${props => props.hasError ? colors.errorLight : colors.primaryBg};
  }
  
  &:disabled {
    background: ${colors.borderLight};
    color: ${colors.textLight};
  }
`;

export const Select = styled.select<{ hasError?: boolean }>`
  width: 100%;
  height: 48px;
  padding: 0 40px 0 16px;
  border: 1px solid ${props => props.hasError ? colors.error : colors.border};
  border-radius: 4px;
  font-size: 16px;
  color: ${colors.text};
  background: ${colors.card};
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%236B6F77' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 20px;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;
  
  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? colors.error : colors.primary};
    box-shadow: 0 0 0 3px ${props => props.hasError ? colors.errorLight : colors.primaryBg};
  }
  
  &:disabled {
    background-color: ${colors.borderLight};
    color: ${colors.textLight};
    cursor: not-allowed;
  }
`;

export const ErrorMessage = styled.span`
  display: block;
  font-size: 12px;
  color: ${colors.error};
  margin-top: 6px;
`;

export const SuccessMessage = styled.span`
  display: block;
  font-size: 12px;
  color: ${colors.success};
  margin-top: 6px;
`;

// ============================================
// TOGGLE INPUT (Money fields with enable/disable)
// ============================================
export const ToggleInputContainer = styled.div<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  height: 48px;
  border: 1px solid ${colors.border};
  border-radius: 4px;
  background: ${props => props.disabled ? colors.borderLight : colors.card};
  overflow: hidden;
  transition: all 0.2s;
`;

export const ToggleInputField = styled.input`
  flex: 1;
  height: 100%;
  padding: 0 16px;
  border: none;
  font-size: 16px;
  color: ${colors.text};
  background: transparent;
  
  &::placeholder {
    color: ${colors.textMuted};
  }
  
  &:focus {
    outline: none;
  }
  
  &:disabled {
    color: ${colors.textMuted};
  }
`;

export const ToggleButton = styled.button<{ enabled?: boolean }>`
  width: 48px;
  height: 100%;
  border: none;
  background: ${props => props.enabled ? colors.successLight : colors.borderLight};
  color: ${props => props.enabled ? colors.success : colors.textMuted};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  
  &:active {
    opacity: 0.7;
  }
`;

// ============================================
// SELECTION CARDS
// ============================================
export const CardGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

export const SelectionCard = styled.button<{ selected?: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 56px;
  padding: 12px 16px;
  border: 1px solid ${props => props.selected ? colors.primary : colors.border};
  border-radius: 4px;
  background: ${props => props.selected ? colors.primary : colors.card};
  cursor: pointer;
  transition: all 0.2s;
  
  &:active {
    transform: scale(0.98);
  }
`;

export const CardIcon = styled.div<{ selected?: boolean }>`
  display: flex;
  align-items: center;
  font-size: 22px;
  color: ${props => props.selected ? 'white' : colors.textLight};
`;

export const CardLabel = styled.span<{ selected?: boolean }>`
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.selected ? 'white' : colors.text};
`;

// ============================================
// VALIDITY PILLS
// ============================================
export const PillContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

export const Pill = styled.button<{ selected?: boolean }>`
  min-height: 40px;
  padding: 8px 16px;
  border: 1px solid ${props => props.selected ? colors.primary : colors.border};
  border-radius: 4px;
  background: ${props => props.selected ? colors.primary : colors.card};
  color: ${props => props.selected ? 'white' : colors.text};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:active {
    transform: scale(0.95);
  }
`;

// ============================================
// BUTTONS
// ============================================
export const ButtonContainer = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 24px;
`;

export const PrimaryButton = styled.button<{ loading?: boolean }>`
  flex: 1;
  min-width: 0;
  height: 46px;
  background: ${colors.primary};
  border: none;
  border-radius: 4px;
  color: white;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  &:not(:disabled):active {
    transform: scale(0.98);
  }
  
  ${props => props.loading && css`
    pointer-events: none;
  `}
`;

export const SecondaryButton = styled.button`
  flex: 1;
  min-width: 0;
  height: 46px;
  background: ${colors.textLight};
  border: none;
  border-radius: 4px;
  color: white;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s;
  
  &:active {
    transform: scale(0.98);
  }
`;

export const NextButton = styled(PrimaryButton)`
  background: ${colors.primary};
`;

// ============================================
// REVIEW CARDS
// ============================================
export const ReviewCard = styled.div`
  background: ${colors.card};
  border: 1px solid ${colors.borderLight};
  border-radius: 6px;
  padding: 16px;
  margin-bottom: 16px;
`;

export const ReviewCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid ${colors.borderLight};
`;

export const ReviewCardTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${colors.primary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const EditButton = styled.button`
  background: none;
  border: 1px solid ${colors.primary};
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  color: ${colors.primary};
  cursor: pointer;
  
  &:active {
    background: ${colors.primaryBg};
  }
`;

export const ReviewRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
`;

export const ReviewLabel = styled.span`
  font-size: 14px;
  color: ${colors.textLight};
`;

export const ReviewValue = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.text};
  text-align: right;
  max-width: 60%;
`;

// ============================================
// TOTAL DISPLAY
// ============================================
export const TotalContainer = styled.div`
  background: ${colors.primaryBg};
  border: 1px solid ${colors.primary};
  border-radius: 6px;
  padding: 16px;
  margin-top: 20px;
`;

export const TotalLabel = styled.span`
  font-size: 14px;
  color: ${colors.textLight};
  text-transform: uppercase;
`;

export const TotalValue = styled.span`
  font-size: 28px;
  font-weight: 700;
  color: ${colors.primary};
  display: block;
  margin-top: 4px;
`;

// ============================================
// LOADING
// ============================================
export const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid ${colors.borderLight};
  border-top-color: ${colors.primary};
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

export const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

export const LoadingText = styled.p`
  font-size: 16px;
  color: ${colors.textLight};
  margin-top: 16px;
`;

// ============================================
// INPUT ROW (Two columns)
// ============================================
export const InputRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 375px) {
    grid-template-columns: 1fr;
    gap: 0;
  }
`;

// ============================================
// AUTO-FILL INDICATOR
// ============================================
export const AutoFillBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: ${colors.success};
  background: ${colors.successLight};
  padding: 2px 8px;
  border-radius: 4px;
  margin-left: 8px;
`;
